import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import '../css/NavigationBar.css'; // NavigationBar 관련 CSS 파일을 import합니다.
import { useApi } from '../utils/apiUtils';
import { useAuthSession } from '../context/AuthSessionContext';
import { OPS_NAV, opsLandingPath, isSuperAdminSession } from './opsNav';
import { BRAND_NAME } from '../config/brand';

// 운영 화면 어디에 있든 상단 "운영 관리" 가 활성으로 보이게 왼쪽 메뉴의 경로 목록으로 판정한다.
const OPS_PATHS = OPS_NAV.flatMap((g) => g.items.map((i) => i.to));

const NavigationBar = () => {
    const [showUserSubMenu, setShowUserSubMenu] = useState(false);
    const [showMessageSubMenu, setShowMessageSubMenu] = useState(false);
    const [menuAccess, setMenuAccess] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedFilter, setSelectedFilter] = useState('all'); // 'all', 'region', 'hospital'
    const navigate = useNavigate();
    const { logout } = useAuthSession();
    const currentPath = window.location.pathname;
    const api = useApi();
    const apiRef = useRef(api); // useApi의 반환값을 useRef에 저장
    const isMounted = useRef(true);
    const filterTimeoutRef = useRef(null);
    const [showTelemedicineSubMenu, setShowTelemedicineSubMenu] = useState(false);
    const [showTeamSubMenu, setShowTeamSubMenu] = useState(false);

    // useApi가 변경될 때마다 ref를 갱신
    useEffect(() => {
        apiRef.current = api;
    }, [api]);

    // 의존성 배열을 빈 배열로 하여 최초 1회만 실행
    const fetchMenuAccess = useCallback(async () => {
        if (!isMounted.current) return;
        try {
            setIsLoading(true);
            const response = await apiRef.current.getMenuAccess();
            console.log('메뉴 접근 권한 응답:', response);
            if (isMounted.current) {
                if (!response.menuAccess) {
                    setMenuAccess({
                        controlCenter: false,
                        dashboard: false,
                        userManagement: false,
                        messageManagement: false,
                        operations: false
                    });
                } else {
                    setMenuAccess(response.menuAccess);
                }
            }
        } catch (error) {
            if (isMounted.current) {
                setMenuAccess({
                    controlCenter: false,
                    dashboard: false,
                    userManagement: false,
                    messageManagement: false,
                    operations: false
                });
            }
        } finally {
            if (isMounted.current) setIsLoading(false);
        }
    }, []); // api를 의존성에서 제거

    useEffect(() => {
        isMounted.current = true;
        fetchMenuAccess();
        return () => {
            isMounted.current = false;
            if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
        };
    }, [fetchMenuAccess]);

    const handleLogout = () => {
        logout();
        navigate('/doctor');
    };

    // 필터 변경 핸들러
    const handleFilterChange = useCallback((filter) => {
        setSelectedFilter(filter);
        
        // 디바운스 처리
        if (filterTimeoutRef.current) {
            clearTimeout(filterTimeoutRef.current);
        }

        filterTimeoutRef.current = setTimeout(() => {
            const filterData = {
                type: filter
            };
            window.dispatchEvent(new CustomEvent('filterChange', { detail: filterData }));
        }, 300);
    }, []);

    // 로그인한 사용자 정보에서 role 확인
    const userInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}');
    const userRole = userInfo.user_role || userInfo.role;
    const isGuardian = userRole === 'guardian';

    if (isGuardian) {
        return (
            <nav className="navigation-bar">
                <ul className="navigation-list">
                    <li>
                        <Link to="/ControlCenter" className="logo-container" style={{ textDecoration: 'none', color: 'inherit' }}>
                            <img className="logo" src="/img/logo.png" alt="로고" />
                            <span className="logo-title">
                                {BRAND_NAME}
                            </span>
                        </Link>
                    </li>
                    <li>
                        <Link 
                            to="/ControlCenter" 
                            className={`navigation-link ${currentPath === '/ControlCenter' ? 'active' : ''}`}
                        >
                            관제센터
                        </Link>
                    </li>
                    <li>
                        <Link 
                            to="/Dashboard" 
                            className={`navigation-link ${currentPath === '/Dashboard' ? 'active' : ''}`}
                        >
                            대시보드
                        </Link>
                    </li>
                    <li>
                        <button onClick={handleLogout} className="sds-btn sds-btn--secondary sds-btn--sm sds-nav__logout">
                        <LogOut size={16} aria-hidden="true" />
                            로그아웃
                        </button>
                    </li>
                </ul>
            </nav>
        );
    }

    // 로딩 중이거나 menuAccess가 아직 설정되지 않은 경우
    if (isLoading || !menuAccess) {
        return (
            <nav className="navigation-bar">
                <ul className="navigation-list">
                    <li>
                        <Link to="/ControlCenter" className="logo-container" style={{ textDecoration: 'none', color: 'inherit' }}>
                            <img className="logo" src="/img/logo.png" alt="로고" />
                            <span className="logo-title">
                                {BRAND_NAME}
                            </span>
                        </Link>
                    </li>
                </ul>
            </nav>
        );
    }

    return (
        <nav className="navigation-bar">            
            <ul className="navigation-list">
                <li>
                    <Link to="/ControlCenter" className="logo-container" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <img className="logo" src="/img/logo.png" alt="로고" />
                        <span className="logo-title">
                            {BRAND_NAME}
                        </span>
                    </Link>
                </li>                
                {menuAccess.controlCenter && (
                    <li>
                        <Link 
                            to="/ControlCenter" 
                            className={`navigation-link ${currentPath === '/ControlCenter' ? 'active' : ''}`}
                        >
                            관제센터
                        </Link>
                    </li>
                )}
                {menuAccess.dashboard && (
                    <li>
                        <Link 
                            to="/Dashboard" 
                            className={`navigation-link ${currentPath === '/Dashboard' ? 'active' : ''}`}
                        >
                            대시보드
                        </Link>
                    </li>
                )}
                {menuAccess.messageManagement && (
                    <li
                        className="navigation-item"
                        onMouseEnter={() => setShowMessageSubMenu(true)}
                        onMouseLeave={() => setShowMessageSubMenu(false)}
                    >
                        <Link 
                            to="/MessageManagement" 
                            className={`navigation-link ${currentPath === '/MessageManagement' ? 'active' : ''}`}
                        >
                            메시지 관리
                        </Link>
                        {showMessageSubMenu && (
                            <ul className="submenu">
                                <li>
                                    <Link 
                                        to="/MessageManagement" 
                                        className={`submenu-link ${currentPath === '/MessageManagement' ? 'active' : ''}`}
                                    >
                                        메세지 목록
                                    </Link>
                                </li>
                                <li>
                                    <Link 
                                        to="/Inquiry" 
                                        className={`submenu-link ${currentPath === '/Inquiry' ? 'active' : ''}`}
                                    >
                                        1:1 문의
                                    </Link>
                                </li>                            
                            </ul>
                        )}
                    </li>
                )}
                {/* {menuAccess.messageManagement && (
                    <li
                        className="navigation-item"
                        onMouseEnter={() => setShowTelemedicineSubMenu(true)}
                        onMouseLeave={() => setShowTelemedicineSubMenu(false)}
                    >
                        <Link 
                            to="/TeleMedicine" 
                            className={`navigation-link ${currentPath.startsWith('/TeleMedicine') ? 'active' : ''}`}
                        >
                            비대면 진료
                        </Link>
                        {showTelemedicineSubMenu && (
                            <ul className="submenu">
                                <li>
                                    <Link 
                                        to="/TeleMedicine" 
                                        className={`submenu-link ${currentPath === '/TeleMedicine' ? 'active' : ''}`}
                                    >
                                        비대면 진료 처리/현황
                                    </Link>
                                </li>
                                <li>
                                    <Link 
                                        to="/TeleMedicineVideo" 
                                        className={`submenu-link ${currentPath === '/TeleMedicineVideo' ? 'active' : ''}`}
                                    >
                                        비대면 화상 진료
                                    </Link>
                                </li>                            
                            </ul>
                        )}
                    </li>
                )}
                {menuAccess.messageManagement && (
                    <li
                        className="navigation-item"
                        onMouseEnter={() => setShowTeamSubMenu(true)}
                        onMouseLeave={() => setShowTeamSubMenu(false)}
                    >
                        <Link 
                            to="/TeamVideoRequest" 
                            className={`navigation-link ${currentPath.startsWith('/TeamVideo') ? 'active' : ''}`}
                        >
                            다학제 팀회의
                        </Link>
                        {showTeamSubMenu && (
                            <ul className="submenu">
                                <li>
                                    <Link 
                                        to="/TeamVideoRequest" 
                                        className={`submenu-link ${currentPath === '/TeamVideoRequest' ? 'active' : ''}`}
                                    >
                                        팀 화상 회의 신청
                                    </Link>
                                </li>
                                <li>
                                    <Link 
                                        to="/TeamVideoStatus" 
                                        className={`submenu-link ${currentPath === '/TeamVideoStatus' ? 'active' : ''}`}
                                    >
                                        팀 화상 회의 처리/현황
                                    </Link>
                                </li>
                                <li>
                                    <Link 
                                        to="/TeamVideoAdd" 
                                        className={`submenu-link ${currentPath === '/TeamVideoAdd' ? 'active' : ''}`}
                                    >
                                        팀 화상 회의 추가
                                    </Link>
                                </li>            
                            </ul>
                        )}
                    </li>
                )} */}
                {menuAccess.operations && (
                    <li className="navigation-item">
                        <Link
                            to={opsLandingPath(isSuperAdminSession())}
                            className={`navigation-link ${OPS_PATHS.includes(currentPath) ? 'active' : ''}`}
                        >
                            운영 관리
                        </Link>
                    </li>
                )}
                {/* <li className="filter-container">
                    <div className="filter-group">
                        <label>
                            <input
                                type="radio"
                                name="filter"
                                value="all"
                                checked={selectedFilter === 'all'}
                                onChange={() => handleFilterChange('all')}
                            />
                            전체
                        </label>
                        <label>
                            <input
                                type="radio"
                                name="filter"
                                value="region"
                                checked={selectedFilter === 'region'}
                                onChange={() => handleFilterChange('region')}
                            />
                            지역
                        </label>
                        <label>
                            <input
                                type="radio"
                                name="filter"
                                value="hospital"
                                checked={selectedFilter === 'hospital'}
                                onChange={() => handleFilterChange('hospital')}
                            />
                            병원
                        </label>
                    </div>
                </li> */}
                <li>
                    <button onClick={handleLogout} className="sds-btn sds-btn--secondary sds-btn--sm sds-nav__logout">
                        <LogOut size={16} aria-hidden="true" />
                        로그아웃
                    </button>
                </li>
            </ul>
        </nav>
    );
};

export default NavigationBar;
